import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AdminShell } from "@project/admin/components/admin-shell";
import { Input } from "@project/components/ui/input";
import { Button } from "@project/components/ui/button";
import { Label } from "@project/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@project/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@project/components/ui/dialog";
import { ChevronLeft, Pencil, Loader2, Package, Boxes } from "lucide-react";
import { gruhUdhyogProductService } from "@project/services/admin/gruh-udhyog-product.service";
import { gruhUdhyogVariantService } from "@project/services/admin/gruh-udhyog-variant.service";
import type { Product, ProductVariant } from "@project/types/gruh-udhyog";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { resolveMediaUrl } from "@project/lib/utils";

interface Props {
  variantId: number;
  productId: number;
}

export default function InventoryDetailPage({ variantId, productId }: Props) {
  const navigate = useNavigate();

  // Product data (fetched fresh by productId from API)
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  // Which variant is currently selected in the right-side panel
  const [activeVariantId, setActiveVariantId] = useState<number>(variantId);

  // Stock Adjustment Modal states
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [stockAction, setStockAction] = useState<"ADD" | "REMOVE">("ADD");
  const [stockQuantity, setStockQuantity] = useState<string>("0");
  const [submittingStock, setSubmittingStock] = useState(false);

  // Fetch the product (with variants) by productId
  const fetchProduct = async () => {
    setLoading(true);
    try {
      const data = await gruhUdhyogProductService.get(productId);
      if (data) {
        setProduct(data);
      } else {
        projectToast.error("Product not found");
      }
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Failed to load product details")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Keep activeVariantId in sync when variantId prop changes
  useEffect(() => {
    setActiveVariantId(variantId);
  }, [variantId]);

  // Derive the currently active variant object from the fetched product
  const variants = product?.variants ?? [];
  const currentVariant: ProductVariant | null =
    variants.find(v => v.id === activeVariantId) ?? variants[0] ?? null;

  // Adjust stock handler
  const handleAdjustStock = async () => {
    if (!currentVariant) {
      return;
    }
    const qty = parseInt(stockQuantity, 10);
    if (Number.isNaN(qty) || qty <= 0) {
      projectToast.error("Please enter a valid quantity greater than 0");
      return;
    }
    setSubmittingStock(true);
    try {
      await gruhUdhyogVariantService.adjustStock(currentVariant.id, {
        stock_action: stockAction,
        stock_quantity: qty,
      });
      projectToast.success(
        `Successfully ${stockAction === "ADD" ? "added" : "removed"} ${qty} items`
      );
      setIsAdjustOpen(false);
      setStockQuantity("0");
      // Refresh product data to get updated stock figures
      await fetchProduct();
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Failed to adjust stock"));
    } finally {
      setSubmittingStock(false);
    }
  };

  const goBack = () => {
    void navigate({
      to: "/admin/_modules/_gruh-udhyog/gruh-udhyog-inventory",
    } as any);
  };

  if (loading) {
    return (
      <AdminShell title="Inventory" subtitle="Manage Your Inventory Items">
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminShell>
    );
  }

  if (!product || !currentVariant) {
    return (
      <AdminShell title="Inventory" subtitle="Manage Your Inventory Items">
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={goBack}
            className="mb-6 flex items-center gap-1.5 rounded-lg border border-border"
          >
            <ChevronLeft className="h-4 w-4" /> Back to Inventory
          </Button>
          <div className="rounded-xl border border-dashed py-12 text-center text-muted-foreground">
            Variant details not found.
          </div>
        </div>
      </AdminShell>
    );
  }

  const productImage = product.image ?? product.image_url ?? null;

  return (
    <AdminShell title="Inventory" subtitle="Manage Your Inventory Items">
      <div className="space-y-6">
        {/* Back Button */}
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={goBack}
            className="flex items-center gap-1.5 rounded-lg border border-border"
          >
            <ChevronLeft className="h-4 w-4" /> Back to Inventory
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* ── Left Column (2/3) ── */}
          <div className="space-y-6 lg:col-span-2">
            {/* Variant Details Card */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-foreground">
                  <span className="font-medium">
                    {currentVariant.value ?? currentVariant.weight ?? "Default"}
                  </span>{" "}
                  Details
                </h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    SKU
                  </p>
                  <p className="mt-0.5 font-mono text-sm text-foreground">
                    {currentVariant.sku ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Total Stock
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-foreground">
                    {currentVariant.total_stock ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Price
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-foreground">
                    ₹{currentVariant.price}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </p>
                  <p
                    className={`mt-0.5 text-sm font-semibold ${currentVariant.is_active ? "text-green-600" : "text-red-500"}`}
                  >
                    {currentVariant.is_active ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>
            </div>

            {/* Stock Details Table Card */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-bold text-foreground">
                  Stock Details
                </h2>
              </div>

              <div className="rounded-lg border border-border">
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      <TableHead className="rounded-tl-lg font-semibold">
                        Name
                      </TableHead>
                      <TableHead className="text-center font-semibold">
                        Total Stock
                      </TableHead>
                      <TableHead className="text-center font-semibold">
                        Available Stock
                      </TableHead>
                      <TableHead className="rounded-tr-lg text-center font-semibold">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium text-foreground">
                        {currentVariant.value ??
                          currentVariant.weight ??
                          "Default"}
                      </TableCell>
                      <TableCell className="text-center">
                        {currentVariant.total_stock ?? 0}
                      </TableCell>
                      <TableCell className="text-center">
                        {currentVariant.available_stock ?? 0}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setStockAction("ADD");
                            setStockQuantity("0");
                            setIsAdjustOpen(true);
                          }}
                          className="h-8 w-8 rounded-lg hover:bg-muted"
                          title="Adjust Stock"
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <div className="mt-3 text-right text-xs text-muted-foreground">
                1-1 of 1 result
              </div>
            </div>
          </div>

          {/* ── Right Column (1/3) ── */}
          <div className="space-y-6">
            {/* Main Product Card */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Main Product
              </h2>
              <div className="flex items-start gap-3">
                <div className="bg-muted/30 flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border">
                  {productImage ? (
                    <img
                      src={resolveMediaUrl(productImage)}
                      alt={product.product_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Package className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="truncate font-bold text-foreground">
                    {product.product_name}
                  </h3>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {product.category_details?.category_name ??
                      product.category_name ??
                      ""}
                  </span>
                </div>
              </div>
            </div>

            {/* Associated Variants Card */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Associated Variants
              </h2>
              <div className="space-y-2">
                {variants.map(v => {
                  const isActive = v.id === activeVariantId;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setActiveVariantId(v.id)}
                      className={`hover:bg-muted/40 flex w-full items-start gap-3 rounded-lg border p-3 text-left transition ${
                        isActive
                          ? "bg-primary/5 border-primary ring-1 ring-primary"
                          : "border-border bg-card"
                      }`}
                    >
                      <div className="bg-muted/40 flex h-8 w-8 shrink-0 items-center justify-center rounded">
                        <Boxes className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-foreground">
                          {v.value ?? v.weight ?? "Default"}
                        </div>
                        <div className="truncate font-mono text-[10px] text-muted-foreground">
                          SKU: {v.sku ?? "—"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Adjust Stock Modal ── */}
      <Dialog open={isAdjustOpen} onOpenChange={setIsAdjustOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              Adjust Stock (Add / Remove)
            </DialogTitle>
          </DialogHeader>

          {currentVariant && (
            <div className="space-y-5 py-2">
              <div>
                <span className="text-sm font-semibold text-muted-foreground">
                  Variant :{" "}
                </span>
                <span className="text-sm font-bold text-foreground">
                  {currentVariant.value ?? currentVariant.weight ?? "Default"}
                </span>
              </div>

              {/* Stock Summary */}
              <div className="grid grid-cols-3 gap-2 rounded-lg border border-amber-900/10 bg-amber-900/5 p-3 text-center">
                <div>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">
                    Total Stock
                  </div>
                  <div className="text-base font-bold text-blue-600">
                    {currentVariant.total_stock ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">
                    Available
                  </div>
                  <div className="text-base font-bold text-green-600">
                    {currentVariant.available_stock ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">
                    Reserved
                  </div>
                  <div className="text-base font-bold text-yellow-600">
                    {currentVariant.reserved_stock ?? 0}
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="text-xs leading-relaxed text-muted-foreground">
                <span className="font-bold text-amber-900">Add (+):</span>{" "}
                Updates Total Stock (and Available).{" "}
                <span className="font-bold text-amber-900">Remove (-):</span>{" "}
                Reduces Available Stock.
              </div>

              {/* Action Type */}
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Adjustment type
                </Label>
                <RadioGroup
                  value={stockAction}
                  onValueChange={(val: "ADD" | "REMOVE") => setStockAction(val)}
                  className="mt-1 flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ADD" id="add" />
                    <Label
                      htmlFor="add"
                      className="cursor-pointer text-sm font-medium"
                    >
                      Add stock (+)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="REMOVE" id="remove" />
                    <Label
                      htmlFor="remove"
                      className="cursor-pointer text-sm font-medium"
                    >
                      Remove stock (-)
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label
                  htmlFor="qty"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Quantity to {stockAction === "ADD" ? "add" : "remove"} *
                </Label>
                <Input
                  id="qty"
                  type="number"
                  min="1"
                  value={stockQuantity}
                  onChange={e => setStockQuantity(e.target.value)}
                  className="rounded-lg border-border"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              disabled={submittingStock}
              onClick={() => setIsAdjustOpen(false)}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              disabled={submittingStock}
              onClick={handleAdjustStock}
              className="rounded-lg bg-amber-900/90 font-semibold text-white hover:bg-amber-900"
            >
              {submittingStock ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                "Apply"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
